if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        if (!this.elements.thumbnails) return;

        this.isSwiping = false;
        this.startX = 0;
        this.startY = 0;
        this.currentX = 0;
        this.currentY = 0;
        this.threshold = 30;

        this.elements.viewer.addEventListener("touchstart", (e) => {
          if (e.touches.length !== 1) return;
          this.startX = e.touches[0].clientX;
          this.startY = e.touches[0].clientY;
          this.isSwiping = false;
        });

        this.elements.viewer.addEventListener(
          "touchmove",
          (e) => {
            if (e.touches.length !== 1) return;
            this.currentX = e.touches[0].clientX;
            this.currentY = e.touches[0].clientY;
            const diffX = this.currentX - this.startX;
            const diffY = this.currentY - this.startY;

            // If horizontal movement is more significant, prevent vertical scroll
            if (Math.abs(diffX) > Math.abs(diffY)) {
              if (e.cancelable) e.preventDefault();
              this.isSwiping = true;
            }
          },
          { passive: false },
        );

        this.elements.viewer.addEventListener("touchend", (e) => {
          if (!this.isSwiping || e.changedTouches.length !== 1) return;

          const direction = this.getSwipeDirection();
          if (direction) {
            this.onSlideChanged(e, direction);
          }

          // Reset coordinates
          this.startX = 0;
          this.startY = 0;
          this.currentX = 0;
          this.currentY = 0;
        });

        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                ),
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      getSwipeDirection() {
        const diffX = this.currentX - this.startX;
        const diffY = this.currentY - this.startY;

        if (
          Math.abs(diffX) > this.threshold &&
          Math.abs(diffX) > Math.abs(diffY)
        ) {
          return diffX > 0 ? "right" : "left";
        }

        return null;
      }

      onSlideChanged(event, direction = null) {
        if (event.type === "touchend" && direction) {
          const mediaId = event.target?.dataset.mediaId;
          let dataTargetMediaId = null;

          if (mediaId) {
            const allThumbnails =
              this.elements.thumbnails.querySelectorAll("[data-target]");

            // find the current thumbnail
            for (const thumbnail of allThumbnails) {
              if (thumbnail.getAttribute("data-target").includes(mediaId)) {
                dataTargetMediaId = thumbnail.getAttribute("data-target");
                break;
              }
            }

            if (dataTargetMediaId) {
              const allArray = Array.from(allThumbnails);
              const currentIndex = allArray.findIndex(
                (t) => t.getAttribute("data-target") === dataTargetMediaId,
              );

              let newIndex = currentIndex;

              if (direction === "left") {
                // move forward until we find a non-hidden image
                while (
                  newIndex < allArray.length - 1 &&
                  allArray[newIndex + 1].classList.contains("hide-image")
                ) {
                  newIndex++;
                }
                newIndex = Math.min(newIndex + 1, allArray.length - 1);
              } else if (direction === "right") {
                // move backward until we find a non-hidden image
                while (
                  newIndex > 0 &&
                  allArray[newIndex - 1].classList.contains("hide-image")
                ) {
                  newIndex--;
                }
                newIndex = Math.max(newIndex - 1, 0);
              }

              const newThumbnail = allArray[newIndex];

              if (
                newThumbnail &&
                !newThumbnail.classList.contains("hide-image")
              ) {
                this.setActiveMedia(newThumbnail.getAttribute("data-target"));
              }
            }
          }
        } else {
          mediaId = event.detail?.currentElement?.dataset?.mediaId;
          this.setActiveMedia(mediaId);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`, //selects active media
        );

        // if not found warn
        if (activeMedia === null) {
          console.warn(`Media with ID ${mediaId} not found in the viewer.`);
          return;
        }

        // get all media and remove active class
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });

        // add active class to the selected media
        activeMedia.classList.add("is-active");

        this.triggerLazyLoading(activeMedia);

        if (prepend) {
          // move the main media element
          activeMedia.parentElement.prepend(activeMedia);

          // check if thumbnails exist and move the corresponding one
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          // reset the slider component to recognize the new order
          if (prepend && this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }

          // --- DOM READ PHASE ---
          const offsetLeft = activeMedia.offsetLeft;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          const isImageInView = activeMediaRect.top > -0.5;
          const scrollYTarget = activeMediaRect.top + window.scrollY;

          // --- DOM WRITE PHASE ---
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: offsetLeft,
            });
          }

          // Don't scroll if the image is already in view
          if (isImageInView) {
            return;
          }

          window.scrollTo({ top: scrollYTarget, behavior: "smooth" });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          return;
        }

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );

        this.setActiveThumbnail(activeThumbnail);

        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset?.mediaPosition,
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // --- DOM READ PHASE ---
        const isVisible = this.elements.thumbnails.isSlideVisible(thumbnail, 10);
        const offsetLeft = thumbnail.offsetLeft;

        // --- DOM WRITE PHASE ---
        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        
        if (isVisible) return;

        this.elements.thumbnails.slider.scrollTo({
          left: offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img",
        );
        if (!image) {
          return;
        }

        const setLiveRegion = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };

        if (image.complete) {
          setLiveRegion();
        } else {
          image.onload = setLiveRegion;
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }

      triggerLazyLoading(activeMedia) {
        const lazyImages = activeMedia.querySelectorAll(
          "img.lazy-image[data-src]",
        );
        lazyImages.forEach((img) => {
          const dataSrc = img.getAttribute("data-src");

          if (dataSrc) {
            img.src = dataSrc;
            img.removeAttribute("data-src");
            img.classList.remove("lazy-image");
          }
        });
      }
    },
  );
}
